import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFlow } from '../../contexts/FlowContext';
import { WebFlowLayout } from '../WebFlowLayout';
import { FlowPrimaryButton } from '../components/FlowPrimaryButton';
import { FlowTextField } from '../components/FlowTextField';
import { StudioFlowColors } from '../studioFlowTheme';
import { SessionAPI } from '../../utils/sessionApi';

export const PhoneScreen: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { state, updateState } = useFlow();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const needsEmail = !state.email.trim();

  const canSubmit =
    state.email.trim().length > 0 &&
    state.phoneNumber.trim().length >= 10 &&
    state.smsConsent &&
    !isSubmitting;

  const ensureSession = async (): Promise<boolean> => {
    if (state.sessionId && state.sessionKey) {
      return true;
    }

    const result = await SessionAPI.createWebSession({
      email: state.email,
      phoneNumber: state.phoneNumber,
      personId: state.personId,
      source: 'web',
    });

    if (!result) {
      return false;
    }

    updateState({
      sessionId: result.id,
      sessionKey: result.key,
    });
    return true;
  };

  const handleContinue = async () => {
    if (!canSubmit) {
      setError('Enter your email, a valid phone number, and accept SMS updates.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const created = await ensureSession();
      if (!created) {
        setError('We could not start your session. Please try again.');
        return;
      }
      navigate('/upload');
    } catch (submitError) {
      console.error(submitError);
      setError('We could not start your session. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const continueButton = (
    <FlowPrimaryButton onClick={handleContinue} disabled={!canSubmit}>
      {isSubmitting ? 'Starting your session…' : 'Start uploading photos'}
    </FlowPrimaryButton>
  );

  return (
    <WebFlowLayout
      title="First, what number should we text your draft to?"
      subtitle="We'll text you when your draft is ready"
    >
      <Box sx={{ pb: { xs: 14, md: 0 } }}>
        {needsEmail && (
          <FlowTextField
            label="Email"
            type="email"
            value={state.email}
            onChange={(event) => updateState({ email: event.target.value })}
            sx={{ mb: 2 }}
          />
        )}

        <FlowTextField
          label="Phone number"
          type="tel"
          value={state.phoneNumber}
          onChange={(event) => updateState({ phoneNumber: event.target.value })}
          placeholder="801-456-1234"
        />

        <FormControlLabel
          sx={{ alignItems: 'flex-start', mt: 2 }}
          control={
            <Checkbox
              checked={state.smsConsent}
              onChange={(event) => updateState({ smsConsent: event.target.checked })}
              sx={{
                color: StudioFlowColors.primary,
                '&.Mui-checked': { color: StudioFlowColors.primary },
              }}
            />
          }
          label={
            <Typography variant="caption" sx={{ color: StudioFlowColors.subtitle }}>
              I agree to receive text updates regarding my subscription (e.g., shipping
              updates and print reminders) at the phone number provided. Message frequency
              varies. Msg &amp; data rates may apply. Reply STOP to unsubscribe.
            </Typography>
          }
        />

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}

        {isDesktop && <Box sx={{ mt: 3 }}>{continueButton}</Box>}
      </Box>
      {!isDesktop && (
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            bgcolor: StudioFlowColors.background,
            px: 3,
            pt: 2,
            pb: 'calc(16px + env(safe-area-inset-bottom))',
          }}
        >
          {continueButton}
        </Box>
      )}
    </WebFlowLayout>
  );
};
